'use client';

import { useRef, useState } from 'react';

import { resolveModeForSsrSource, type SsrThemeSource, type ThemeMode } from './ssrTheme';

export interface ResolvedSsrThemeModeInput {
  source: SsrThemeSource;
  ssrTheme: ThemeMode;
}

/**
 * Keeps the client theme aligned with the root layout's SSR payload.
 *
 * Uses the React "adjust state during render" pattern so a no-cookie
 * light preference is applied in the same turn ThemeProvider first
 * commits — before its `useEffect` writes inline dark tokens (WEB-693).
 *
 * Server-driven changes (`source` / `ssrTheme`) adopt the new payload.
 * User toggles via `setMode` are left alone until the next server change
 * (e.g. leaving `/admin` for a cookie-backed `/clt` route).
 */
export function useResolvedSsrThemeMode(
  input: ResolvedSsrThemeModeInput,
): [ThemeMode, (mode: ThemeMode) => void] {
  const { source, ssrTheme } = input;
  const [mode, setMode] = useState<ThemeMode>(source === 'forced' ? 'dark' : ssrTheme);
  const serverKey = `${source}:${ssrTheme}`;
  const prevServerKeyRef = useRef<string | null>(null);

  if (prevServerKeyRef.current !== serverKey) {
    prevServerKeyRef.current = serverKey;
    const next = resolveModeForSsrSource(source, ssrTheme);
    if (next !== mode) {
      setMode(next);
    }
  } else if (source === 'forced' && mode !== 'dark') {
    setMode('dark');
  }

  return [mode, setMode];
}
